<template>
    <div class="page-container">
        <SystemMessage type="error"
                       v1
                       :message="`${errorMessage || 'Something went wrong'}, Please refresh the page and try again.`"
                       v-if="error"
                       :dismissButton="false"></SystemMessage>
        <Filters
            :reportID="reportID"
            :selectedApps="selectedApps"
            :selectedCountries="selectedCountries"
            :selectedAdSources="selectedAdSources"
            :selectedAB="selectedAB"
            :selectedAdUnit="adUnitKey"
            :dateRange="dateRange"
            :selectedBreak="breakBy"
            :isIncludeApps="isIncludeApps"
            :allowDisclaimer="allowDisclaimer"
            :isIncludeCountries="isIncludeCountries"
            :isIncludeAdSources="isIncludeAdSources"
            datePresetList="date1-7-14-30-90"
            :allowedDays="90"
            @dateRangePresetNameChanged="dateRangePresetName = $event"
            @filterChanged="filterChanged"
        ></Filters>
        <div class="page-data-container-background">
            <div class="page-data-container">
                <div class="tabs-area-container">
                    <div class="tabs-container">
                        <Tabs
                            :tabOptions="adUnitTabs"
                            :selectedTab="selectedAdUnit"
                            @selectionChanged="selectedAdUnitChanged"></Tabs>
                    </div>
                </div>
                <div class="chart-area-container">
                    <div class="report-data-title-container">
                        <span class="font-3 report-data-title">
                            {{loading ? 'Searching for data...' : `${adUnitName}, ${dateRangePresetName || timeFrame}`}}
                        </span>
                        <MetricSet :selectedIndex.sync="selectedMetricSetIndex"
                                   v-if="metricSets && selectedAdUnit !== adUnitIds.all"
                                   :metricSets="metricSets"
                                   :adUnit="selectedAdUnit"
                                   :availableKpis="availableKPIs"
                                   @metricSetsSaved="metricSetsSaved"
                                   @metricSetsSaveError="metricSetsSaveError"></MetricSet>
                    </div>
                    <KPIs
                        :isToday="isToday"
                        :currency="currency"
                        :previousTimeFrame="previousTimeFrame"
                        :kpisArr="kpisData"
                        :selectedKPI="selectedKPI"
                        v-on:update:selectedKPI="selectedKPIChanged"
                        :showArrows="!isOverview"
                        :page.sync="selectedMetricSetIndex"
                    ></KPIs>
                    <div class="chart-and-sort-container"
                         id="breakAndSort">
                        <BreakAndSort :breakOptions="filteredBreakByOptions"
                                      :breakBy="breakBy"
                                      :sortBy="sortBy"
                                      :chartType="chartType"
                                      :chartLoading="loading || !chartData"
                                      :hiddenChartTypes="hiddenChartTypes"
                                      @chartTypeChanged="chartTypeChanged"
                                      @breakChanged="breakChanged"
                                      @sortChanged="sortChanged"
                        ></BreakAndSort>
                        <chart :data="chartData"
                               :prefix="chartSymbols.prefix"
                               :suffix="chartSymbols.suffix"
                               :type="chartType"
                               :loading="loading"
                               :group="breakBy"
                               :totalMode="isColumnChart"
                               :mean="isSelectedKPIMean"
                               :preserveSelection="preserveSelection"
                               animate
                               :showAllHAxisValues="isColumnChart">
                            <span slot="noDataOverlaySubText" v-if="abFilterError">
                                <span>To view reports filtered by A/B, you must select a single application which is or has run an A/B test</span>
                            </span>
                            <span slot="noDataOverlaySubText" v-else>
                                <span>To learn more about in-app bidding with LevelPlay, </span>
                                <a :href="links.levelPlay" target="_blank">click here</a>
                            </span>
                        </chart>
                    </div>
                </div>
                <Table v-if="!loading && rows && columns && breakBy !== noneBreakByKey"
                       :columns="columns"
                       :rows="rows"
                       :footer="footer"
                       :sortDirection="tableSortDirection"
                       :sortBy="tableSortBy"
                       class="bidding-table"
                       @sortChanged="tableSortChanged"></Table>
            </div>
            <ReportsDisclaimer paymentMessage dateInUtc estimateTermsAndConditions class="report-disclaimer" />
        </div>
    </div>
</template>

<script>
import { mapMutations, mapActions } from 'vuex';
import Chart from 'components/charts/Chart.vue';
import BreakAndSort from 'monetize/reports/components/BreakAndSort.vue';
import Table from 'components/table/Table.vue';
import links from 'services/metaData/links';
import consts from 'services/metaData/consts';
import { breakByOptions, tabs } from 'monetize/reports/bidding/biddingReportConsts';
import kpiSymbolMapping from 'monetize/reports/utils/kpiSymbolMapping';
import currencySymbolMap from 'services/utils/currencySymbolMap';
import reportsPersistence from '@/pages/monetize/reports/services/reportsPersistenceService';
import metricSetsPersistence from '@/pages/monetize/reports/services/metricSetsPersistence';
import Tabs from 'components/tabs/Tabs.vue';
import { adUnitsMap, adUnitIds } from 'services/utils/adUnitsMap';
import KPIs from 'monetize/reports/components/kpis/KPIs.vue';
import MetricSet from 'monetize/reports/components/metricSet/MetricSet.vue';
import ReportsDisclaimer from 'monetize/reports/components/ReportsDisclaimer.vue';
import Filters from 'monetize/reports/components/Filters.vue';
import { dashedDateFormat, monthNameDayYearFormat } from 'services/utils/dates';
import moment from 'moment';
import kpisTextMapping, { meanSuffix } from 'monetize/reports/utils/kpisTextMapping';
import { pluralize } from 'services/utils/strings';
import SystemMessage from 'components/SystemMessage/SystemMessage.vue';
import http from 'services/utils/http';
// eslint-disable-next-line import/no-cycle
import { paths } from '@/router';
import chartTypes from 'components/charts/chartTypes';
import dateServices from 'components/dateRange/DateServices';

const adUnitsBreakById = 1;
const applicationBreakByKey = breakByOptions.find((option) => option.id === 3).key;
const reportType = 'bidding';
const noneBreakByKey = 'none';
const adSourceBreakKey = 'adSource';
// This is two weeks back if counting in today
const defaultDaysBack = 13;

const adUnitTabs = tabs.map((adUnitKey) => {
    const adUnit = adUnitsMap[adUnitKey];

    return {
        key: adUnitKey,
        text: adUnit.displayName,
        icon: adUnit.icon,
        iconWidth: 14,
        iconHeight: 24
    };
});

const defaultSortingMethod = 'revenue';
const sortingMethods = [defaultSortingMethod, 'name'];
const anyKey = 'any';

const cacheMinutes = 5;

const allBiddingAdUnitsFilter = [adUnitIds.rewardedVideo, adUnitIds.interstitial, adUnitIds.banner]
    .map((adUnit) => `units[]=${adUnit}`).join('&');

export default {
    name: 'BiddingReportsPage',
    components: {
        SystemMessage,
        Filters,
        ReportsDisclaimer,
        MetricSet,
        KPIs,
        Tabs,
        Table,
        BreakAndSort,
        Chart
    },
    data() {
        return {
            error: false,
            errorMessage: '',
            chartData: undefined,
            sortBy: defaultSortingMethod,
            breakBy: applicationBreakByKey,
            dateRangePresetName: undefined,
            loading: false,
            chartType: chartTypes.LineChart,
            rows: undefined,
            columns: undefined,
            footer: undefined,
            tableSortBy: 'revenue',
            tableSortDirection: 'desc',
            currency: 'USD',
            selectedKPI: undefined,
            preserveSelection: true,
            reportPersistenceKey: undefined,
            mediationTopPreviousData: undefined,
            mediationTopData: undefined,
            mediationTopDataCancelToken: undefined,
            availableKPIs: {},
            adUnitTabs,
            kpis: [],
            selectedAdUnit: adUnitIds.all,
            links,
            isToday: false,
            previousTimeFrame: '',
            timeFrame: '',
            reportID: 'biddingReport',
            dateRange: {
                from: moment().subtract(defaultDaysBack, 'days').toDate(),
                to: new Date()
            },
            allowDisclaimer: false,
            isIncludeApps: true,
            isIncludeCountries: true,
            isIncludeAdSources: true,
            selectedCountries: [],
            selectedAdSources: [],
            selectedApps: [],
            selectedAB: consts.AB_OBJ[0].key,
            selectedMetricSetIndex: 0,
            metricSets: undefined
        };
    },
    computed: {
        adUnitIds() {
            return adUnitIds;
        },
        isOverview() {
            return this.selectedAdUnit === adUnitIds.all;
        },
        abFilterError() {
            return this.selectedAB !== anyKey && (this.selectedApps.length !== 1 || !this.isIncludeApps);
        },
        noneBreakByKey() {
            return noneBreakByKey;
        },
        adUnitMapItem() {
            return adUnitsMap[this.selectedAdUnit];
        },
        adUnitName() {
            return this.adUnitMapItem.displayName;
        },
        adUnitKey() {
            return this.adUnitMapItem.key;
        },
        filteredBreakByOptions() {
            return this.selectedAdUnit === adUnitIds.all
                ? breakByOptions : breakByOptions.filter((option) => option.id !== adUnitsBreakById);
        },
        isColumnChart() {
            return this.chartType === chartTypes.ColumnChart;
        },
        currencySymbol() {
            return currencySymbolMap[this.currency];
        },
        symbolMapping() {
            return kpiSymbolMapping(this.currencySymbol);
        },
        chartSymbols() {
            const kpiSymbol = this.symbolMapping[this.selectedKPI];
            let prefix;
            let suffix;

            if (kpiSymbol && kpiSymbol.location === 'pre') {
                prefix = kpiSymbol.symbol;
            }

            if (kpiSymbol && kpiSymbol.location === 'post') {
                suffix = kpiSymbol.symbol;
            }

            return { prefix, suffix };
        },
        isSelectedKPIMean() {
            const kpiData = kpisTextMapping[this.selectedKPI];

            return kpiData && kpiData.suffix === meanSuffix;
        },
        hiddenChartTypes() {
            if (this.isSelectedKPIMean) {
                return [chartTypes.ComboChart];
            }

            return [];
        },
        metricSetsForSelectedAdUnit() {
            if (!this.metricSets) return null;

            return this.metricSets[this.selectedAdUnit];
        },
        kpisData() {
            const metricSets = this.metricSetsForSelectedAdUnit;
            const kpisInAMetricSet = 5;

            if (!metricSets) {
                return [];
            }

            return metricSets.map(({ metricsData }) => Array(kpisInAMetricSet).fill().map((_, index) => {
                const kpi = metricsData[index];
                const value = this.mediationTopData?.kpis?.[kpi]?.value;
                const hasValue = typeof value !== 'undefined';

                return kpi ? {
                    id: kpi,
                    value,
                    previousValue: hasValue
                        && this.mediationTopPreviousData ? this.mediationTopPreviousData[kpi].value : undefined,
                    disabled: !hasValue || this.isKPIDisabled(kpi)
                } : undefined;
            })).flat();
        }
    },
    async created() {
        this.setPage({
            pageTitle: 'Bidding Reports'
        });
        this.reportPersistenceKey = await reportsPersistence.getKey();
        await this.loadPersistenceData();

        try {
            this.loading = true;
            const [{ data: availableKPIs }, { data: metricSetsResponse }] = await Promise.all([
                this.httpGetWithCache(`/api/rest/v1/partners/MetricSet/availableKPIs?reportType=${reportType}`),
                this.httpGetWithCache(`/api/rest/v1/partners/MetricSet?reportType=${reportType}`)
            ]);
            this.availableKPIs = availableKPIs;
            this.setMetricSets(metricSetsResponse);
            const adUnitMetricsSets = this.metricSets[adUnitIds[this.adUnitKey]];
            const persistentIndex = await metricSetsPersistence
                .getDataFromLocalStorage(adUnitIds[this.adUnitKey]);
            if (persistentIndex <= (adUnitMetricsSets.length - 1)) {
                this.selectedMetricSetIndex = persistentIndex;
                const { metricsData } = adUnitMetricsSets[this.selectedMetricSetIndex];
                this.selectedKPI = this.getFirstAvailableKPI(metricsData);
            }

            this.$watch('selectedMetricSetIndex', () => {
                this.fetchData();
                metricSetsPersistence
                    .saveSelectedMetricSet(adUnitIds[this.adUnitKey], this.selectedMetricSetIndex);
            });
            this.fetchData();
        } catch (err) {
            this.loading = false;
            this.availableKPIs = {};
            this.metricSets = undefined;
            this.error = true;
        }
    },
    methods: {
        ...mapMutations({
            setPage: 'setPage'
        }),
        ...mapActions({
            getWithCache: 'cache/getWithCache'
        }),
        httpGetWithCache(url, { refresh = false, cancelToken, ttl } = {}) {
            return this.getWithCache({
                api: this.reportID,
                req: url,
                refresh,
                ttl,
                cancelToken
            });
        },
        setMetricSets(metricSetsResponse) {
            this.metricSets = Object.entries(metricSetsResponse.metricSets)
                .reduce((acc, [adUnit, metricSet]) => ({
                    ...acc,
                    [adUnit]: metricSet.sort(this.sortMetricSetsByDefault)
                }), {});

            const metricSetsForAdUnit = this.metricSets[this.selectedAdUnit];
            if (this.selectedMetricSetIndex > metricSetsForAdUnit.length - 1) {
                this.selectedMetricSetIndex = 0;
            }
        },
        metricSetsSaved(metricSetsResponse) {
            const oldMetricSetsForAdUnit = this.metricSetsForSelectedAdUnit;
            const oldSelectedMetricSetIndex = this.selectedMetricSetIndex;
            this.setMetricSets(metricSetsResponse);

            const metricSetsForAdUnit = this.metricSets[this.selectedAdUnit];
            const hasNewMetrics = metricSetsForAdUnit[this.selectedMetricSetIndex].metricsData
                .some((kpi) => !oldMetricSetsForAdUnit[this.selectedMetricSetIndex].metricsData.includes(kpi));

            if (hasNewMetrics && oldSelectedMetricSetIndex === this.selectedMetricSetIndex) {
                this.fetchData({ resetKpi: false });
            }
        },
        metricSetsSaveError(error) {
            this.errorMessage = error?.response?.data?.error || 'Could not save metric sets';
            this.error = true;
        },
        /**
         * Loads the persistence data from the url and localStorage
         * @returns {Promise<void>}
         */
        async loadPersistenceData() {
            const reportsData = (await reportsPersistence
                .getReportDataFromLocalStorage(this.reportPersistenceKey, reportType)) || {};

            const {
                from,
                to,
                breakBy,
                graphBreak,
                adUnit,
                app,
                adSource,
                sortingMethod,
                country,
                testGroup
            } = { ...this.$route.params, ...reportsData };

            if (from && to) {
                const fromDate = moment(from, dashedDateFormat).toDate();
                const today = moment(dateServices.getCurrentTimeUTC());
                const daysDiff = today.diff(fromDate, 'days') + 1;
                if (daysDiff <= 90) {
                    this.dateRange = {
                        from: fromDate,
                        to: moment(to, dashedDateFormat).toDate()
                    };
                }
            }

            if (breakBy) {
                const [, otherBreak] = breakBy.split(',');

                if (otherBreak === undefined) {
                    this.breakBy = noneBreakByKey;
                } else {
                    const breakOption = breakByOptions.find((breakByOption) => breakByOption.key === otherBreak);

                    if (breakOption) {
                        this.breakBy = otherBreak || noneBreakByKey;
                    } else {
                        this.breakBy = noneBreakByKey;
                    }
                }
            }

            if (testGroup) {
                this.selectedAB = testGroup.toLowerCase();
            }

            if (graphBreak) {
                this.graphBreak = graphBreak;
            }

            if (adUnit) {
                this.selectedAdUnit = (adUnit === anyKey || adUnit.toString() === adUnitIds.offerwall.toString())
                    ? adUnitIds.all : Number(adUnit);
            }

            if (app) {
                [this.selectedApps, this.isIncludeApps] = this.persistenceStringToArray(app);
            }

            if (adSource) {
                [this.selectedAdSources, this.isIncludeAdSources] = this.persistenceStringToArray(adSource);
                this.selectedAdSources = this.selectedAdSources.filter((as) => as.endsWith('_Bidding'));
            }

            if (country) {
                [this.selectedCountries, this.isIncludeCountries] = this.persistenceStringToArray(country);
            }

            if (sortingMethod) {
                this.sortBy = sortingMethods.includes(sortingMethod) ? sortingMethod : defaultSortingMethod;
            }
        },
        arrayToPersistenceString(arr, includeMode = true) {
            return arr.length === 0 ? anyKey : `${includeMode ? '' : 'EX_'}${arr.join(',')}`;
        },
        persistenceStringToArray(persistenceString) {
            const includeMode = !persistenceString.startsWith('EX_');
            const formattedString = includeMode ? persistenceString : persistenceString.replace('EX_', '');
            const arr = formattedString === anyKey ? [] : formattedString.split(',');

            return [arr, includeMode];
        },
        savePersistentState() {
            const graphBreak = 'date';
            const from = moment(this.dateRange.from).format(dashedDateFormat);
            const to = moment(this.dateRange.to).format(dashedDateFormat);

            const persistentData = {
                from,
                to,
                breakBy: this.breakBy !== noneBreakByKey ? `${graphBreak},${this.breakBy}` : 'date',
                graphBreak,
                adUnit: this.selectedAdUnit === adUnitIds.all ? anyKey : this.selectedAdUnit.toString(),
                app: this.arrayToPersistenceString(this.selectedApps, this.isIncludeApps),
                adSource: this.arrayToPersistenceString(this.selectedAdSources, this.isIncludeAdSources),
                sortingMethod: this.sortBy,
                country: this.arrayToPersistenceString(this.selectedCountries, this.isIncludeCountries),
                testGroup: this.selectedAB !== anyKey ? this.selectedAB.toUpperCase() : anyKey
            };

            this.updateUrl(persistentData);

            reportsPersistence.saveReportData(this.reportPersistenceKey, persistentData);
        },
        updateUrl({
            from,
            to,
            breakBy,
            graphBreak,
            adUnit,
            app,
            adSource,
            sortingMethod,
            country,
            testGroup
        }) {
            const url = `${paths.monetize.reports.bidding}/from/${from}/to/${to}/break/${breakBy}/graphBreak/`
                + `${graphBreak}/adUnit/${adUnit}/app/${app}/country/${country}/adSource/${adSource}/testGroup/`
                + `${testGroup}/sort/${sortingMethod}`;

            if (this.$route.path !== url) {
                this.$router.replace(url).catch((error) => {
                    const navigationCancelledErrorType = 3;

                    // ignore navigation cancellation errors
                    if (error.type !== navigationCancelledErrorType) {
                        throw error;
                    }
                });
            }
        },
        sortChanged(sortBy) {
            this.sortBy = sortBy;
            this.fetchData({ resetKpi: false });
        },
        breakChanged(breakBy) {
            this.breakBy = breakBy;
            this.fetchData({ resetKpi: false });
        },
        chartTypeChanged(newType) {
            this.chartType = newType;
        },
        tableSortChanged(sortId, sortDirection) {
            this.tableSortBy = sortId;
            this.tableSortDirection = sortDirection;
        },
        selectedAdUnitChanged(adUnit) {
            this.selectedAdUnit = adUnit;
            this.selectedMetricSetIndex = 0;
            if (!this.filteredBreakByOptions.some((breakBy) => breakBy.key === this.breakBy)) {
                this.breakBy = noneBreakByKey;
            }

            this.fetchData({ resetKpi: false, preserveSelection: false });
        },
        filterChanged({
            field,
            value,
            isIncludeField,
            isIncludeMode
        }) {
            this[field] = value;

            if (isIncludeField) {
                this[isIncludeField] = isIncludeMode;
            }

            this.fetchData({ resetKpi: false, preserveSelection: false });
        },
        generateMediationTopDataUrl({ returnOnlyKpis, fromDate, toDate } = {}) {
            return `/api/rest/v1/partners/statistics/mediationTopData?${[
                'injectSumMean=true&isBidder=true',
                returnOnlyKpis ? 'returnOnlyKpis=true' : 'showAllPossibleRecords=1&top=all',
                'breakdowns[]=date',
                this.breakBy !== noneBreakByKey && `breakdowns[]=${this.breakBy}`,
                `fields=${this.kpis.join(',')}`,
                `fromDate=${fromDate.format(dashedDateFormat)}&toDate=${toDate.format(dashedDateFormat)}`,
                this.selectedAdUnit !== adUnitIds.all ? `units=${this.selectedAdUnit}` : allBiddingAdUnitsFilter,
                ...this.selectedAdSources.map((adSource) => `adSource[]=${adSource}`),
                ...this.selectedCountries.map((country) => `countries[]=${country}`),
                ...this.selectedApps.map((app) => `apps[]=${app}`),
                this.selectedAdSources.length && !this.isIncludeAdSources && 'adSourceMode=exclude',
                this.selectedCountries.length && !this.isIncludeCountries && 'countriesMode=exclude',
                this.selectedApps.length && !this.isIncludeApps && 'appsMode=exclude',
                `testGroup=${this.selectedAB === anyKey ? this.selectedAB : this.selectedAB.toUpperCase()}`,
                `sortingMethod=${this.sortBy}`
            ].filter(Boolean).join('&')}`;
        },
        formatDatesRangeString(from, to) {
            return `${from.format(monthNameDayYearFormat)} - ${to.format(monthNameDayYearFormat)}`;
        },
        async fetchData({ resetKpi = true, preserveSelection = true } = {}) {
            this.chartData = undefined;
            this.mediationTopData = undefined;
            this.mediationTopPreviousData = undefined;
            this.preserveSelection = preserveSelection;
            this.rows = undefined;
            this.columns = undefined;
            this.footer = undefined;

            if (!this.metricSetsForSelectedAdUnit) return;

            this.kpis = this.metricSetsForSelectedAdUnit[this.selectedMetricSetIndex].metricsData;
            const fromDate = moment(this.dateRange.from);
            const toDate = moment(this.dateRange.to);
            const daysDiff = toDate.diff(fromDate, 'days') + 1;
            const previousTo = moment(fromDate).subtract(1, 'days');
            const previousFrom = moment(previousTo).subtract(daysDiff, 'days');
            this.timeFrame = this.formatDatesRangeString(fromDate, toDate);

            const mediationTopDataUrl = this.generateMediationTopDataUrl({
                fromDate,
                toDate
            });

            if (this.mediationTopDataCancelToken) {
                this.mediationTopDataCancelToken.cancel();
            }

            this.savePersistentState();

            if (this.abFilterError) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const cancelToken = http.createCancelToken();
            this.mediationTopDataCancelToken = cancelToken;

            const mediationTopDataPromises = [this.httpGetWithCache(mediationTopDataUrl, {
                cancelToken,
                ttl: cacheMinutes
            })];

            if (daysDiff <= 90) {
                this.previousTimeFrame = this.formatDatesRangeString(previousFrom, previousTo);
                const mediationTopPreviousDataUrl = this.generateMediationTopDataUrl({
                    fromDate: previousFrom,
                    toDate: previousTo,
                    returnOnlyKpis: true
                });

                mediationTopDataPromises.push(this.httpGetWithCache(mediationTopPreviousDataUrl, {
                    cancelToken,
                    ttl: cacheMinutes
                }).catch(() => ({})));
            }

            try {
                const [
                    { data: mediationTopData },
                    { data: mediationTopPreviousData } = {}] = await Promise.all(mediationTopDataPromises);
                this.mediationTopPreviousData = mediationTopPreviousData;
                this.mediationTopData = mediationTopData;
                if (resetKpi || !this.kpis.includes(this.selectedKPI) || this.isKPIDisabled(this.selectedKPI)) {
                    this.selectedKPI = this.getFirstAvailableKPI(this.kpis);
                }

                this.currency = mediationTopData.currency;
                this.setTableData(this.kpis, this.mediationTopData);
                this.chartData = this.mediationTopData[this.selectedKPI];
                this.loading = false;
                this.mediationTopDataCancelToken = undefined;
            } catch (error) {
                const noData = error?.response?.data?.code === 404;
                const isCancel = http.isCancel(error);

                if (isCancel) return;

                this.loading = false;

                if (!noData) {
                    this.error = true;
                    this.errorMessage = error?.response?.data?.error;
                }
            }
        },
        setTableData(kpis, data) {
            const totalsRowId = 'totals';
            const eCPMKPI = 'eCPM';
            const breakBy = data?.[this.selectedKPI]?.sum;

            if (!Array.isArray(breakBy)) return;

            const tableKpis = kpis.filter((kpi) => data[kpi].sum.length === breakBy.length);
            [this.tableSortBy] = tableKpis;
            const leftColumnName = breakByOptions.find(({ key }) => key === this.breakBy).displayText;

            const columns = [
                {
                    id: this.breakBy,
                    text: leftColumnName
                },
                ...tableKpis.map((kpi) => ({ id: kpi, text: kpi === eCPMKPI ? kpi : kpisTextMapping[kpi].name }))
            ];

            const rows = breakBy.map(({ id, displayName }, breakIndex) => [
                {
                    id,
                    value: displayName,
                    text: displayName
                },
                ...tableKpis.map((kpi) => {
                    const { value } = data[kpi].sum[breakIndex];
                    const kpiSymbol = this.symbolMapping[kpi];
                    let text = value.toLocaleString();

                    if (kpiSymbol) {
                        if (kpiSymbol.location === 'pre') {
                            text = `${kpiSymbol.symbol} ${text}`;
                        }

                        if (kpiSymbol.location === 'post') {
                            text = `${text} ${kpiSymbol.symbol}`;
                        }
                    }

                    return {
                        id: `${kpi}_${id}`,
                        text,
                        value
                    };
                })
            ]);

            const footer = rows.filter((row) => row[0].id === totalsRowId)
                .map(([, ...values]) => [
                    {
                        id: totalsRowId,
                        text: `Total: ${rows.length - 1} ${pluralize(leftColumnName)}`
                    }, ...values]).flat();

            this.columns = columns;
            this.rows = rows.filter((row) => row[0].id !== totalsRowId);
            this.footer = footer;
        },
        sortMetricSetsByDefault(a, b) {
            if (a.isDefault) {
                return -1;
            }

            if (b.isDefault) {
                return 1;
            }

            return a - b;
        },
        selectedKPIChanged(kpi) {
            const { metricsData } = this.metricSetsForSelectedAdUnit[this.selectedMetricSetIndex];
            const newKPI = !this.isKPIDisabled(kpi) ? kpi : this.getFirstAvailableKPI(metricsData);
            this.selectedKPI = newKPI;

            if (this.mediationTopData && this.selectedKPI) {
                this.preserveSelection = true;
                this.chartData = this.mediationTopData[newKPI];
            }
        },
        isKPIDisabled(kpi) {
            const breakByAdSource = this.breakBy === adSourceBreakKey;

            return breakByAdSource && kpi === kpisTextMapping.revenueSOV.key;
        },
        getFirstAvailableKPI(kpis) {
            return kpis.find((kpi) => !this.isKPIDisabled(kpi));
        }
    },
    watch: {
        hiddenChartTypes(hiddenChartTypes) {
            if (hiddenChartTypes.includes(this.chartType)) {
                this.chartType = chartTypes.LineChart;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";

.page-container {
    display: flex;
    flex-direction: column;
    line-height: initial;
    background: $light-200;
}

.page-data-container-background {
    flex: 1;
}

.page-data-container {
    margin: 20px;
    background: $white;
    box-shadow: 2px 2px 2px 0 rgba(0,0,0,.1);
}

.chart-area-container {
    padding: 15px;
    border-radius: $borderRadius;
}

.chart-and-sort-container {
    border: $border;
    border-top: none;
    padding: 15px;
}

.tabs-area-container {
    border-bottom: $border;
}

.tabs-container {
    max-width: 60%;
}

.bidding-table {
    padding: 15px;
    padding-bottom: 10px;
}

.report-data-title-container {
    display: flex;
    justify-content: space-between;
}

.report-data-title {
    padding-bottom: 17px;
    line-height: 25px;
    display: inline-block;
}

.report-disclaimer {
    margin-left: 20px;
    margin-bottom: 20px;
}
</style>
